
import $ from 'jquery';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GdkMultipleSelectBox } from "@gdk/multiple-select-box";
import { GdkDatePicker } from "@gdk/datepicker";
import PageHeader from '../PageHeader/PageHeader';
import Layout from "../../components/Layout";
import { searchAgents } from "../../api/search";
import { UserContext } from "../../context/UserContextProvider";
import { EMAIL_WITH_TLD_REGEX, US_PHONE_REGEX } from '../../constants/ValidationConstants';
import FloridaCountyOptions from '../GeaoOnboarding/constants/options/FloridaCountyOptions';
import { UsStateOptions } from '../../constants/options/UsStateOptions';
import { classes } from '../../util/style-utils';
import { GetClientConfiguration } from "../../api/client-configuration";
/**
 * Helper function to generate form field class names with optional error styling.
 * @param {string} baseClasses - The base CSS classes for the form field
 * @param {boolean} hasError - Whether the field has a validation error
 * @returns {string} The combined class names
 */
const formFieldClasses = (baseClasses, hasError) => classes(baseClasses, hasError && "form-field--error");

const validationSchema = yup.object({
    agentCode: yup.string().required("Agent code is required."),
    email: yup.string()
        .email("Please enter a valid email address")
        .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address")
        .required("Email address is required."),
    residentAddressLine1: yup.string().required("Please enter the first line of the resident address."),
    residentAddressLine2: yup.string().nullable().optional(),
    residentAddressCity: yup.string().required("Please enter the resident city."),
    residentAddressState: yup.string().required("Please enter the resident state.").max(2, "State must be two letters.").matches(/[a-zA-Z]+/g,
        { message: "State must contain only letters.", excludeEmptyString: true }),
    residentAddressZip: yup.string().required("Please enter the resident ZIP code."),
    residentAddressCounty: yup.string().nullable().optional(),
    mailingAddressLine1: yup.string().nullable().optional(),
    mailingAddressLine2: yup.string().nullable().optional(),
    mailingAddressCity: yup.string().nullable().optional(),
    mailingAddressState: yup.string().nullable().optional(),
    mailingAddressZip: yup.string().nullable().optional(),
    effectiveDateOfTermination: yup.date()
        .required("Please enter the effective date of termination.")
        .typeError("Must be a valid date.")
        .min(new Date(new Date().setHours(0, 0, 0, 0)), "Effective date cannot be in the past."),
    terminationReason: yup.string().required("Please select a reason for termination."),
    isMovingToAnotherAgency: yup.boolean().optional(),
    movingAgencyInfoName: yup.boolean().optional(),
    movingAgencyInfoAddress: yup.boolean().optional(),
    movingAgencyInfoPhone: yup.boolean().optional(),
    movingAgencyInfoNotAvailable: yup.boolean().optional(),
    newAgencyName: yup.string()
        .when(['movingAgencyInfoName', 'movingAgencyInfoNotAvailable'], ([infoName, notAvailable], schema) =>
            infoName && !notAvailable
                ? schema.required("Agency name is required.")
                : schema.nullable().optional()
        ),
    newAgencyStreet: yup.string()
        .when(['movingAgencyInfoAddress', 'movingAgencyInfoNotAvailable'], ([infoAddress, notAvailable], schema) =>
            infoAddress && !notAvailable
                ? schema.required("Street address is required.")
                : schema.nullable().optional()
        ),
    newAgencyUnit: yup.string().nullable().optional(),
    newAgencyCity: yup.string()
        .when(['movingAgencyInfoAddress', 'movingAgencyInfoNotAvailable'], ([infoAddress, notAvailable], schema) =>
            infoAddress && !notAvailable
                ? schema.required("City is required.")
                : schema.nullable().optional()
        ),
    newAgencyState: yup.string()
        .when(['movingAgencyInfoAddress', 'movingAgencyInfoNotAvailable'], ([infoAddress, notAvailable], schema) =>
            infoAddress && !notAvailable
                ? schema.required("State is required.")
                : schema.nullable().optional()
        ),
    newAgencyZip: yup.string()
        .when(['movingAgencyInfoAddress', 'movingAgencyInfoNotAvailable'], ([infoAddress, notAvailable], schema) =>
            infoAddress && !notAvailable
                ? schema.required("ZIP code is required.").matches(/^\d{5}$/, "ZIP code must be 5 digits.")
                : schema.nullable().optional().matches(/^\d{5}$/, { message: "ZIP code must be 5 digits.", excludeEmptyString: true })
        ),
    newAgencyPhone: yup.string()
        .when(['movingAgencyInfoPhone', 'movingAgencyInfoNotAvailable'], ([infoPhone, notAvailable], schema) =>
            infoPhone && !notAvailable
                ? schema.required("Phone number is required.").matches(US_PHONE_REGEX, "Please enter a valid phone number in (xxx) xxx-xxxx format.")
                : schema.nullable().optional().matches(US_PHONE_REGEX, { message: "Please enter a valid phone number in (xxx) xxx-xxxx format.", excludeEmptyString: true })
        ),
});

export default function AgentTerminationForm() {
    const user = useContext(UserContext);
    const navigate = useNavigate();
    const [clientConfiguration, setClientConfiguration] = useState();

    useEffect(() => {
        GetClientConfiguration(response => {
            setClientConfiguration(response);
        });
    }, []);

    const [agentProfiles, setAgentProfiles] = useState([]);
    const [selectedAgentProfile, setSelectedAgentProfile] = useState({});
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [enableButtons, setEnableButtons] = useState(true);
    const [showAgencyCheckboxError, setShowAgencyCheckboxError] = useState(false);

    const agentSelectBoxRef = useRef();
    const agentSelectBoxGdkRef = useRef();
    const terminationDatePickerRef = useRef();

    const defaultValues = {
        agentCode: '',
        email: '',
        residentAddressLine1: '',
        residentAddressLine2: '',
        residentAddressCity: '',
        residentAddressState: '',
        residentAddressZip: '',
        residentAddressCounty: '',
        mailingAddressLine1: '',
        mailingAddressLine2: '',
        mailingAddressCity: '',
        mailingAddressState: '',
        mailingAddressZip: '',
        mailingSameAsAgentResidence: false,
        effectiveDateOfTermination: '',
        terminationReason: '',
        isMovingToAnotherAgency: false,
        movingAgencyInfoName: false,
        movingAgencyInfoAddress: false,
        movingAgencyInfoPhone: false,
        movingAgencyInfoNotAvailable: false,
        newAgencyName: '',
        newAgencyStreet: '',
        newAgencyUnit: '',
        newAgencyCity: '',
        newAgencyState: '',
        newAgencyZip: '',
        newAgencyPhone: '',
    };

    const { register, handleSubmit, watch, formState: { errors }, reset, getValues, setValue } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        defaultValues: defaultValues
    });

    useEffect(() => {
        terminationDatePickerRef.current = new GdkDatePicker({
            content: "#effective-date-of-termination",
            monthYearDropdowns: true,
            minDate: new Date(),
        });
    }, []);

    const watchResidentAddressState = watch('residentAddressState', selectedAgentProfile.addresses?.residentAddress?.state);
    const watchTerminationReason = watch('terminationReason');
    const watchMovingAgencyInfoName = watch('movingAgencyInfoName');
    const watchMovingAgencyInfoAddress = watch('movingAgencyInfoAddress');
    const watchMovingAgencyInfoPhone = watch('movingAgencyInfoPhone');
    const watchMovingAgencyInfoNotAvailable = watch('movingAgencyInfoNotAvailable');
    const watchMailingSameAsAgentResidence = watch('mailingSameAsAgentResidence');

    useEffect(() => {
        if (watchMailingSameAsAgentResidence) {
            setValue('mailingAddressLine1', getValues('residentAddressLine1'));
            setValue('mailingAddressLine2', getValues('residentAddressLine2'));
            setValue('mailingAddressCity', getValues('residentAddressCity'));
            setValue('mailingAddressState', getValues('residentAddressState'));
            setValue('mailingAddressZip', getValues('residentAddressZip'));
        }
    }, [watchMailingSameAsAgentResidence]);

    const isMovingToAnotherAgency = !!clientConfiguration?.agentTerminationReasons?.[watchTerminationReason]
        ?.toLowerCase().includes('moving to another agency');

    const hasAgencyInfoSelected = watchMovingAgencyInfoName || watchMovingAgencyInfoAddress || watchMovingAgencyInfoPhone || watchMovingAgencyInfoNotAvailable;

    useEffect(() => {
        if (hasAgencyInfoSelected) setShowAgencyCheckboxError(false);
    }, [hasAgencyInfoSelected]);

    const onAgentSelectBoxChange = () => {
        const newAgentId = agentSelectBoxRef.current.value;
        setSelectedAgentId(newAgentId);
    };

    useEffect(() => {
        agentSelectBoxGdkRef.current = new GdkMultipleSelectBox({ "content": "#agent-profile-select-box" });
        $("#agent-profile-select-box").on("change", onAgentSelectBoxChange);
    }, []);

    useEffect(() => {
        searchAgents({ agencyId: user?.agencyId }, response => setAgentProfiles(response));
    }, []);

    useEffect(() => {
        setSelectedAgentProfile(agentProfiles.find(p => p.id === selectedAgentId) ?? {});
    }, [selectedAgentId, agentProfiles]);

    useEffect(() => {
        reset();
        mapAgentProfileToFormValues(selectedAgentProfile);
    }, [selectedAgentProfile]);

    const mapAgentProfileToFormValues = (agentProfile) => {
        setValue('agentCode', agentProfile.id ?? "");
        setValue('email', agentProfile.email ?? "");
        setValue('residentAddressLine1', agentProfile.addresses?.residentAddress?.street1 ?? "");
        setValue('residentAddressLine2', agentProfile.addresses?.residentAddress?.street2 ?? "");
        setValue('residentAddressCity', agentProfile.addresses?.residentAddress?.city ?? "");
        setValue('residentAddressState', agentProfile.addresses?.residentAddress?.state ?? "");
        setValue('residentAddressZip', agentProfile.addresses?.residentAddress?.zipCode ?? "");
        setValue('residentAddressCounty', agentProfile.addresses?.residentAddress?.county ?? "");
        setValue('mailingAddressLine1', agentProfile.addresses?.mailingAddress?.street1 ?? "");
        setValue('mailingAddressLine2', agentProfile.addresses?.mailingAddress?.street2 ?? "");
        setValue('mailingAddressCity', agentProfile.addresses?.mailingAddress?.city ?? "");
        setValue('mailingAddressState', agentProfile.addresses?.mailingAddress?.state ?? "");
        setValue('mailingAddressZip', agentProfile.addresses?.mailingAddress?.zipCode ?? "");
    }

    useEffect(() => {
        reset();
        setSelectedAgentProfile({});
        setSelectedAgentId('');
    }, [reset]);

    const getTerminationReasons = (clientConfiguration) => {
        if (clientConfiguration?.agentTerminationReasons) {
            return Object.getOwnPropertyNames(clientConfiguration.agentTerminationReasons)
        }
        return [];
    }

    const onSubmit = (values) => {
        if (isMovingToAnotherAgency && !hasAgencyInfoSelected) {
            setShowAgencyCheckboxError(true);
            return;
        }
        if (!values.errors && selectedAgentProfile.id) {
            const residentAddress = {
                street1: values.residentAddressLine1,
                street2: values.residentAddressLine2,
                city: values.residentAddressCity,
                state: values.residentAddressState,
                zipCode: values.residentAddressZip,
                county: values.residentAddressCounty
            };

            const terminationProfile = {
                agentId: selectedAgentProfile.id,
                email: values.email,
                addresses: {
                    residentAddress,
                    mailingAddress: {
                        street1: values.mailingAddressLine1,
                        street2: values.mailingAddressLine2,
                        city: values.mailingAddressCity,
                        state: values.mailingAddressState,
                        zipCode: values.mailingAddressZip
                    }
                },
                terminationInfo: {
                    effectiveDate: values.effectiveDateOfTermination,
                    reason: values.terminationReason,
                    movingToAnotherAgency: isMovingToAnotherAgency,
                    movingAgencyInfoName: values.movingAgencyInfoName,
                    movingAgencyInfoAddress: values.movingAgencyInfoAddress,
                    movingAgencyInfoPhone: values.movingAgencyInfoPhone,
                    movingAgencyInfoNotAvailable: values.movingAgencyInfoNotAvailable,
                    movingAgencyInfo: isMovingToAnotherAgency ? {
                        name: values.newAgencyName,
                        street: values.newAgencyStreet,
                        unit: values.newAgencyUnit,
                        city: values.newAgencyCity,
                        state: values.newAgencyState,
                        zip: values.newAgencyZip,
                        phone: values.newAgencyPhone,
                    } : undefined
                }
            };

            console.log("Termination Profile:", { agentId: terminationProfile.agentId });
            reset();
            setEnableButtons(true);
        }
    };

    const formatPhoneNumber = (e) => {
        const digits = e.target.value.replaceAll(/\D/g, '').slice(0, 10);
        let formatted;
        if (digits.length <= 3) formatted = digits.length ? `(${digits}` : '';
        else if (digits.length <= 6) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        setValue('newAgencyPhone', formatted, { shouldValidate: true });
    };

    const onClearAll = () => {
        setSelectedAgentId(null);
        setSelectedAgentProfile({});
        $("#agent-profile-select-box").val("").trigger("change");
        reset();
    };

    const onCancel = () => {
        navigate(-1);
    };

    return (
        <Layout>
            <PageHeader header="Agent Termination Request" showBottomBorder={false}>
                <p>Please complete all required fields to initiate termination process.</p>
            </PageHeader>
            <div className="container margin-top">
                {/* Agent Selector - outside form */}
                <div className="form-field">
                    <label htmlFor="agent-profile-select-box" className="text">Name</label>
                    <div>
                        <select id="agent-profile-select-box" ref={agentSelectBoxRef}>
                            <option value="">Please select agent profile</option>
                            {agentProfiles?.map(agentProfile => (
                                <option value={agentProfile.id} key={agentProfile.id}>
                                    {AgentFullName(agentProfile)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={formFieldClasses("form-field large-text-input", errors.agentCode)}>
                        <label htmlFor="agentCode" className="text">Agent Code</label>
                        <input
                            id="agentCode"
                            type="text"
                            size="30"
                            {...register("agentCode", { value: selectedAgentProfile.agentCode ?? "" })}
                        />
                        {errors.agentCode && <span className="form-message">{errors.agentCode.message}</span>}
                    </div>

                    <div className={formFieldClasses("form-field large-text-input", errors.email)}>
                        <label htmlFor="email" className="text">Agent Email Address</label>
                        <input
                            id="email"
                            type="email"
                            size="50"
                            {...register("email", { value: selectedAgentProfile.email ?? "" })}
                        />
                        {errors.email && <span className="form-message">{errors.email.message}</span>}
                    </div>

                    <fieldset className="form-fieldset">
                        <legend className="text form-fieldset__legend">Agent Residential Address</legend>

                        <div className={formFieldClasses("form-field large-text-input", errors.residentAddressLine1)}>
                            <label htmlFor="residence-line1" className="text">Address Line 1</label>
                            <input
                                id="residence-line1"
                                type="text"
                                size="30"
                                {...register("residentAddressLine1", { value: selectedAgentProfile.addresses?.residentAddress?.street1 ?? "" })}
                            />
                            {errors.residentAddressLine1 && <span className="form-message">{errors.residentAddressLine1.message}</span>}
                        </div>

                        <div className={formFieldClasses("form-field large-text-input", errors.residentAddressLine2)}>
                            <label htmlFor="residence-line2" className="text">Address Line 2</label>
                            <input
                                id="residence-line2"
                                type="text"
                                size="30"
                                {...register("residentAddressLine2", { value: selectedAgentProfile.addresses?.residentAddress?.street2 ?? "" })}
                            />
                            {errors.residentAddressLine2 && <span className="form-message">{errors.residentAddressLine2.message}</span>}
                        </div>

                        <div className="row">
                            <div className="col-sm-12 col-md-6">
                                <div className={formFieldClasses("form-field large-text-input", errors.residentAddressCity)}>
                                    <label htmlFor="residence-city" className="text">City</label>
                                    <input
                                        id="residence-city"
                                        type="text"
                                        size="30"
                                        {...register("residentAddressCity", { value: selectedAgentProfile.addresses?.residentAddress?.city ?? "" })}
                                    />
                                    {errors.residentAddressCity && <span className="form-message">{errors.residentAddressCity.message}</span>}
                                </div>
                            </div>
                            <div className="col-sm-12 col-md-6">
                                <div className={formFieldClasses("form-field", errors.residentAddressState)}>
                                    <label htmlFor="residence-state" className="text">State</label>
                                    <input
                                        id="residence-state"
                                        type="text"
                                        size="5"
                                        {...register("residentAddressState", { value: selectedAgentProfile.addresses?.residentAddress?.state ?? "" })}
                                    />
                                    {errors.residentAddressState && <span className="form-message">{errors.residentAddressState.message}</span>}
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12 col-md-6">
                                <div className={formFieldClasses("form-field", errors.residentAddressZip)}>
                                    <label htmlFor="residence-zip" className="text">ZIP Code</label>
                                    <input
                                        id="residence-zip"
                                        type="text"
                                        size="15"
                                        {...register("residentAddressZip", { value: selectedAgentProfile.addresses?.residentAddress?.zipCode ?? "" })}
                                    />
                                    {errors.residentAddressZip && <span className="form-message">{errors.residentAddressZip.message}</span>}
                                </div>
                            </div>
                            <div className="col-sm-12 col-md-6">
                                <div className={formFieldClasses("form-field", errors.residentAddressCounty)}>
                                    <label htmlFor="residence-county" className="text">County</label>
                                    {watchResidentAddressState?.toUpperCase() === "FL" ? (
                                        <div className="select-box">
                                            <select
                                                id="residence-county"
                                                {...register("residentAddressCounty", { value: selectedAgentProfile.addresses?.residentAddress?.county ?? "" })}
                                            >
                                                <option value="">Select a county</option>
                                                {FloridaCountyOptions.map(option => (
                                                    <option value={option.value} key={option.value}>{option.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    ) : (
                                        <input
                                            id="residence-county"
                                            type="text"
                                            size="30"
                                            {...register("residentAddressCounty", { value: selectedAgentProfile.addresses?.residentAddress?.county ?? "" })}
                                        />
                                    )}
                                    {errors.residentAddressCounty && <span className="form-message">{errors.residentAddressCounty.message}</span>}
                                </div>
                            </div>
                        </div>

                    </fieldset>

                    <fieldset className="form-fieldset">
                        <legend className="text form-fieldset__legend">Agent Mailing Address</legend>

                        <fieldset className="form-field checkbox-group">
                            <div className="checkbox-wrapper col-1">
                                <div className="label--description">
                                    <input
                                        id="mailing-same-as-agent-residence"
                                        type="checkbox"
                                        {...register("mailingSameAsAgentResidence")}
                                    />
                                    <label htmlFor="mailing-same-as-agent-residence" className="checkbox">
                                        <span>Same as Agent Residential Address</span>
                                    </label>
                                </div>
                            </div>
                        </fieldset>

                        <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressLine1)}>
                            <label htmlFor="mailing-line1" className="text">Address Line 1</label>
                            <input
                                id="mailing-line1"
                                type="text"
                                size="30"
                                {...register("mailingAddressLine1", { value: selectedAgentProfile.addresses?.mailingAddress?.street1 ?? "" })}
                            />
                            {errors.mailingAddressLine1 && <span className="form-message">{errors.mailingAddressLine1.message}</span>}
                        </div>

                        <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressLine2)}>
                            <label htmlFor="mailing-line2" className="text">Address Line 2</label>
                            <input
                                id="mailing-line2"
                                type="text"
                                size="30"
                                {...register("mailingAddressLine2", { value: selectedAgentProfile.addresses?.mailingAddress?.street2 ?? "" })}
                            />
                            {errors.mailingAddressLine2 && <span className="form-message">{errors.mailingAddressLine2.message}</span>}
                        </div>

                        <div className="row">
                            <div className="col-sm-12 col-md-6">
                                <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressCity)}>
                                    <label htmlFor="mailing-city" className="text">City</label>
                                    <input
                                        id="mailing-city"
                                        type="text"
                                        size="30"
                                        {...register("mailingAddressCity", { value: selectedAgentProfile.addresses?.mailingAddress?.city ?? "" })}
                                    />
                                    {errors.mailingAddressCity && <span className="form-message">{errors.mailingAddressCity.message}</span>}
                                </div>
                            </div>
                            <div className="col-sm-12 col-md-6">
                                <div className={formFieldClasses("form-field large-text-input", errors.mailingAddressState)}>
                                    <label htmlFor="mailing-state" className="text">State</label>
                                    <input
                                        id="mailing-state"
                                        type="text"
                                        size="5"
                                        {...register("mailingAddressState", { value: selectedAgentProfile.addresses?.mailingAddress?.state ?? "" })}
                                    />
                                    {errors.mailingAddressState && <span className="form-message">{errors.mailingAddressState.message}</span>}
                                </div>
                            </div>
                        </div>

                        <div className={formFieldClasses("form-field", errors.mailingAddressZip)}>
                            <label htmlFor="mailing-zip" className="text">ZIP Code</label>
                            <input
                                id="mailing-zip"
                                type="text"
                                size="15"
                                {...register("mailingAddressZip", { value: selectedAgentProfile.addresses?.mailingAddress?.zipCode ?? "" })}
                            />
                            {errors.mailingAddressZip && <span className="form-message">{errors.mailingAddressZip.message}</span>}
                        </div>
                    </fieldset>

                    <div className={formFieldClasses("form-field", errors.effectiveDateOfTermination)}>
                        <label htmlFor="effective-date-of-termination" className="text">Effective Date of Termination</label>
                        <input
                            id="effective-date-of-termination"
                            className="date date-datepicker"
                            type="tel"
                            placeholder="MM/DD/YYYY"
                            aria-label="Date. Please enter 2 digit month, 2 digit day, and 4 digit year"
                            {...register("effectiveDateOfTermination", { valueAsDate: true })}
                        />
                        {errors.effectiveDateOfTermination && <span className="form-message">{errors.effectiveDateOfTermination.message}</span>}
                    </div>

                    <div className={formFieldClasses("form-field", errors.terminationReason)}>
                        <label htmlFor="termination-reason" className="text">Reason for Termination</label>
                        <div className="select-box">
                            <select
                                id="termination-reason"
                                {...register("terminationReason")}
                            >
                                <option value="">Select a reason</option>
                                {getTerminationReasons(clientConfiguration).map((key) => (
                                    <option value={key} key={key}>
                                        {clientConfiguration?.agentTerminationReasons[key]}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {errors.terminationReason && <span className="form-message">{errors.terminationReason.message}</span>}
                    </div>

                    {isMovingToAnotherAgency && (
                        <fieldset className="form-fieldset">
                            <legend className="text form-fieldset__legend">Agency Information</legend>
                            <p>Do you have any of the following information on the Agency they are moving to?</p>

                            <fieldset className="form-field checkbox-group">
                                <div className="checkbox-wrapper col-1">
                                    <div className="label--description">
                                        <input
                                            id="moving-agency-info-name"
                                            type="checkbox"
                                            {...register("movingAgencyInfoName")}
                                            onChange={(e) => {
                                                if (e.target.checked) setValue('movingAgencyInfoNotAvailable', false);
                                                register("movingAgencyInfoName").onChange(e);
                                            }}
                                        />
                                        <label htmlFor="moving-agency-info-name" className="checkbox">
                                            <span>Agency name</span>
                                        </label>
                                    </div>
                                    <div className="label--description">
                                        <input
                                            id="moving-agency-info-address"
                                            type="checkbox"
                                            {...register("movingAgencyInfoAddress")}
                                            onChange={(e) => {
                                                if (e.target.checked) setValue('movingAgencyInfoNotAvailable', false);
                                                register("movingAgencyInfoAddress").onChange(e);
                                            }}
                                        />
                                        <label htmlFor="moving-agency-info-address" className="checkbox">
                                            <span>Agency address</span>
                                        </label>
                                    </div>
                                    <div className="label--description">
                                        <input
                                            id="moving-agency-info-phone"
                                            type="checkbox"
                                            {...register("movingAgencyInfoPhone")}
                                            onChange={(e) => {
                                                if (e.target.checked) setValue('movingAgencyInfoNotAvailable', false);
                                                register("movingAgencyInfoPhone").onChange(e);
                                            }}
                                        />
                                        <label htmlFor="moving-agency-info-phone" className="checkbox">
                                            <span>Agency phone number</span>
                                        </label>
                                    </div>
                                    <div className="label--description">
                                        <input
                                            id="moving-agency-info-not-available"
                                            type="checkbox"
                                            {...register("movingAgencyInfoNotAvailable")}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setValue('movingAgencyInfoName', false);
                                                    setValue('movingAgencyInfoAddress', false);
                                                    setValue('movingAgencyInfoPhone', false);
                                                }
                                                register("movingAgencyInfoNotAvailable").onChange(e);
                                            }}
                                        />
                                        <label htmlFor="moving-agency-info-not-available" className="checkbox">
                                            <span>I do not have information about the other Agency</span>
                                        </label>
                                    </div>
                                </div>
                            </fieldset>
                            {showAgencyCheckboxError && (
                                <span className="form-message" style={{ color: 'red' }}>Please select at least one of the above options.</span>
                            )}

                            {watchMovingAgencyInfoName && !watchMovingAgencyInfoNotAvailable && (
                                <div className={formFieldClasses("form-field large-text-input", errors.newAgencyName)}>
                                    <label htmlFor="new-agency-name" className="text">Agency Name</label>
                                    <input
                                        id="new-agency-name"
                                        type="text"
                                        size="50"
                                        {...register("newAgencyName")}
                                    />
                                    {errors.newAgencyName && <span className="form-message">{errors.newAgencyName.message}</span>}
                                </div>
                            )}

                            {watchMovingAgencyInfoAddress && !watchMovingAgencyInfoNotAvailable && (
                                <>
                                    <div className={formFieldClasses("form-field large-text-input", errors.newAgencyStreet)}>
                                        <label htmlFor="new-agency-street" className="text">Street Address</label>
                                        <input
                                            id="new-agency-street"
                                            type="text"
                                            size="50"
                                            {...register("newAgencyStreet")}
                                        />
                                        {errors.newAgencyStreet && <span className="form-message">{errors.newAgencyStreet.message}</span>}
                                    </div>
                                    <div className={formFieldClasses("form-field large-text-input", errors.newAgencyUnit)}>
                                        <label htmlFor="new-agency-unit" className="text">Unit/Number</label>
                                        <input
                                            id="new-agency-unit"
                                            type="text"
                                            size="20"
                                            {...register("newAgencyUnit")}
                                        />
                                        {errors.newAgencyUnit && <span className="form-message">{errors.newAgencyUnit.message}</span>}
                                    </div>
                                    <div className={formFieldClasses("form-field large-text-input", errors.newAgencyCity)}>
                                        <label htmlFor="new-agency-city" className="text">City</label>
                                        <input
                                            id="new-agency-city"
                                            type="text"
                                            size="30"
                                            {...register("newAgencyCity")}
                                        />
                                        {errors.newAgencyCity && <span className="form-message">{errors.newAgencyCity.message}</span>}
                                    </div>
                                    <div className="row">
                                        <div className="col-sm-12 col-md-6">
                                            <div className={formFieldClasses("form-field", errors.newAgencyState)}>
                                                <label htmlFor="new-agency-state" className="text">State</label>
                                                <div className="select-box">
                                                    <select
                                                        id="new-agency-state"
                                                        {...register("newAgencyState")}
                                                    >
                                                        <option value="">Select a state</option>
                                                        {UsStateOptions.map(option => (
                                                            <option value={option.value} key={option.value}>{option.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {errors.newAgencyState && <span className="form-message">{errors.newAgencyState.message}</span>}
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-6">
                                            <div className={formFieldClasses("form-field", errors.newAgencyZip)}>
                                                <label htmlFor="new-agency-zip" className="text">ZIP Code</label>
                                                <input
                                                    id="new-agency-zip"
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={5}
                                                    size="10"
                                                    {...register("newAgencyZip")}
                                                />
                                                {errors.newAgencyZip && <span className="form-message">{errors.newAgencyZip.message}</span>}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {watchMovingAgencyInfoPhone && !watchMovingAgencyInfoNotAvailable && (
                                <div className={formFieldClasses("form-field", errors.newAgencyPhone)}>
                                    <label htmlFor="new-agency-phone" className="text">Agency Phone Number</label>
                                    <input
                                        id="new-agency-phone"
                                        type="tel"
                                        placeholder="(xxx) xxx-xxxx"
                                        size="20"
                                        {...register("newAgencyPhone")}
                                        onChange={formatPhoneNumber}
                                    />
                                    {errors.newAgencyPhone && <span className="form-message">{errors.newAgencyPhone.message}</span>}
                                </div>
                            )}
                        </fieldset>
                    )}

                    <div className="row">
                        <div className="col-sm-12">
                            <button
                                type="submit"
                                className="btn btn--primary btn--full-mobile btn--pull-right"
                                disabled={!enableButtons || !selectedAgentProfile.id}
                                onClick={() => {
                                    if (isMovingToAnotherAgency && !hasAgencyInfoSelected) {
                                        setShowAgencyCheckboxError(true);
                                    }
                                }}
                            >
                                <span>Submit</span>
                            </button>
                            <button
                                type="button"
                                className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                                onClick={onCancel}
                                disabled={!enableButtons}
                            >
                                <span>Cancel</span>
                            </button>
                            <button
                                type="button"
                                className="btn btn--secondary btn--full-mobile btn--pull-right"
                                onClick={onClearAll}
                                disabled={!enableButtons}
                            >
                                <span>Clear All</span>
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    );
}

export const AgentFullName = (agentProfile) => [
    agentProfile?.firstName,
    agentProfile?.middleName,
    agentProfile?.lastName,
    agentProfile?.suffix
].filter(Boolean).join(" ");